import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {CnTraceStepDto} from '../../../model/dto/api/cn-trace-step.dto';

/**
* 'Herleitung'-Expander for a CommissionExpectation calculationTrace (FR-14).
*
* Parses the backend calculationTrace JSON string into a readable step list:
*   base -> Satz -> Faktoren (K / Recurring / Dynamik) -> gewähltes Tier-Band ->
*   min/max-Clamp -> brutto -> Storno -> netto.
*
* NFR-CleanCore: the FE performs NO calculation. Every value is taken verbatim from
* the trace; this component only formats and displays what the backend computed.
*
* Accepts either a raw JSON string (`trace`) or an already-parsed step array (`steps`),
* plus optional gross/net summary values for the headline.
*/
@Component({
    selector: 'mvs-cn-expectation-trace',
    templateUrl: './cn-expectation-trace.component.html',
    styleUrls: ['./cn-expectation-trace.component.scss'],
    standalone: false
})
export class CnExpectationTraceComponent implements OnInit, OnChanges, OnDestroy {

    /** Raw calculationTrace JSON string (as stored on CommissionExpectation). */
    @Input() trace: string | null;

    /** Already-parsed trace steps (alternative to `trace`). */
    @Input() steps: CnTraceStepDto[] | null;

    /** Optional headline values (backend-computed). */
    @Input() grossAmount?: number;
    @Input() netAmount?: number;

    /**
     * Optional top-level "Warum dieser Betrag?" summary (WP-BE-2 / Simone W3). When the caller passes
     * the result summary explicitly it wins; otherwise it is parsed from the trace JSON envelope
     * (`{summary, steps:[…]}`). Backend-computed plain-language German — the FE only displays it.
     */
    @Input() summary?: string | null;

    /** Optional title shown on the accordion header. */
    @Input() title: string = 'Herleitung';

    /** Start collapsed by default. */
    @Input() expanded: boolean = false;

    parsedSteps: CnTraceStepDto[] = [];
    /** Resolved "Warum dieser Betrag?" summary line (from @Input summary or the parsed trace envelope). */
    resolvedSummary: string | null = null;
    parseError: boolean = false;
    initialized: boolean = false;

    /**
     * Sprechende German labels per machine stepKind — mirrors CommissionStepKind.uiLabel on the backend.
     * Used as a fallback when a step carries no explicit `label`, so the raw enum code is never shown.
     */
    private static readonly STEP_KIND_LABELS: { [kind: string]: string } = {
        BASE: 'Bemessungsbasis',
        RATE: 'Satz',
        TIER: 'Staffel',
        FACTOR_K: 'Faktor Tarifvariante',
        FACTOR_RECURRING: 'Faktor wiederkehrend',
        FACTOR_DYNAMIC: 'Faktor Dynamik',
        MIN_MAX_CLAMP: 'Min/Max-Begrenzung',
        ROUNDING: 'Rundung',
        CANCELLATION_NET: 'Storno-Netto',
        CONDITION: 'Bedingung',
        HOOK: 'Logik-Hook'
    };

    /**
     * Sprechende German operation wording per machine stepKind, shown in the 'Faktor / Operation' column
     * instead of a raw operator token. Steps that only carry a factor (or none) fall back to the factor value.
     */
    private static readonly STEP_KIND_OPERATIONS: { [kind: string]: string } = {
        BASE: 'Grundlage',
        RATE: 'Satz angewendet',
        TIER: 'Staffel gewählt',
        FACTOR_K: 'Faktor',
        FACTOR_RECURRING: 'Faktor',
        FACTOR_DYNAMIC: 'Faktor',
        MIN_MAX_CLAMP: 'Begrenzt',
        ROUNDING: 'Gerundet',
        CANCELLATION_NET: 'Storno abgezogen',
        CONDITION: 'Bedingung geprüft',
        HOOK: 'Logik ausgeführt'
    };

    ngOnInit(): void {
        this.initComponent();
    }

    /**
    * Initialize the trace expander: parse the supplied trace / steps for display.
    */
    initComponent(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['trace'] || changes['steps'] || changes['summary']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }

    /**
    * Build the displayable step list (and the "Warum dieser Betrag?" summary) from the supplied inputs.
    */
    refreshComponent(): void {
        this.parseError = false;
        this.resolvedSummary = null;

        if (this.steps && this.steps.length) {
            this.parsedSteps = this.steps;
            // An explicit summary input wins when steps are supplied directly.
            this.resolvedSummary = this.summary ?? null;
            return;
        }

        this.parsedSteps = this.parseTrace(this.trace);
    }

    /**
    * Parse the calculationTrace JSON string into a step array and extract the top-level summary.
    * Tolerates both the `{summary, steps:[...]}` envelope (WP-BE-2) and a bare top-level array `[...]`.
    * An explicit `summary` @Input always takes precedence over the envelope value.
    */
    private parseTrace(raw: string | null | undefined): CnTraceStepDto[] {
        if (!raw) {
            this.resolvedSummary = this.summary ?? null;
            return [];
        }
        try {
            const parsed = JSON.parse(raw);
            const list = Array.isArray(parsed) ? parsed : parsed?.steps;
            const envelopeSummary = Array.isArray(parsed) ? null : parsed?.summary;
            this.resolvedSummary = this.summary ?? envelopeSummary ?? null;
            if (!Array.isArray(list)) {
                return [];
            }
            return list.map((item: any): CnTraceStepDto => ({
                stepKind: item.stepKind ?? item.kind,
                label: item.label ?? item.stepLabel,
                inputValue: item.inputValue ?? item.input,
                factor: item.factor,
                operator: item.operator ?? item.op,
                outputValue: item.outputValue ?? item.output,
                explanation: item.explanation,
            }));
        } catch {
            this.parseError = true;
            this.resolvedSummary = this.summary ?? null;
            return [];
        }
    }

    /** True when there is something to render. */
    hasSteps(): boolean {
        return this.parsedSteps && this.parsedSteps.length > 0;
    }

    /** True when a "Warum dieser Betrag?" summary line should be shown. */
    hasSummary(): boolean {
        return !!this.resolvedSummary && this.resolvedSummary.trim().length > 0;
    }

    /**
    * Sprechende German label for a step — prefers the backend-supplied `label`, falls back to a mapped
    * stepKind label, and only as a last resort returns the raw stepKind so nothing is ever blank.
    */
    stepLabel(step: CnTraceStepDto): string {
        if (step.label && step.label.trim().length) {
            return step.label;
        }
        const mapped = step.stepKind ? CnExpectationTraceComponent.STEP_KIND_LABELS[step.stepKind] : undefined;
        return mapped ?? step.stepKind ?? '–';
    }

    /**
    * Sprechende German operation wording for the 'Faktor / Operation' column, derived from the stepKind
    * instead of exposing a raw operator code. Returns null when no wording applies (the factor is shown alone).
    */
    operationLabel(step: CnTraceStepDto): string | null {
        if (!step.stepKind) {
            return null;
        }
        return CnExpectationTraceComponent.STEP_KIND_OPERATIONS[step.stepKind] ?? null;
    }
}
